// Plain-language wording for account health, sync gaps and Google OAuth outcomes.
import { useMemo } from "react";
import type { AccountSummary } from "@shared/api";
import { accountLabels } from "@web/ui/AccountChip";
import { useAccounts } from "./queries";
import { formatShortDateTime } from "./time";

export type HealthTone = "ok" | "attention" | "paused" | "working";

export interface AccountHealth {
  tone: HealthTone;
  label: string;
  explanation: string | null;
}

export function accountHealth(account: AccountSummary): AccountHealth {
  switch (account.status) {
    case "needs_reauth":
      return {
        tone: "attention",
        label: "Needs reconnection",
        explanation:
          "Google stopped accepting MeeMail's access for this account (a password change or a revoked grant does this). Reconnect to pick up where it left off.",
      };
    case "scope_missing":
      return {
        tone: "attention",
        label: "Missing permission",
        explanation:
          "Google didn't grant every permission MeeMail asks for. Reconnect and tick every checkbox on the consent screen.",
      };
    case "disabled":
      return {
        tone: "paused",
        label: "Paused",
        explanation: "MeeMail isn't syncing this account. Its mail stays in Gmail, untouched.",
      };
    case "ok":
      break;
  }
  switch (account.syncStatus) {
    case "never":
    case "bootstrapping":
      return {
        tone: "working",
        label: "First sync in progress",
        explanation:
          "MeeMail is reading recent mail for the first time. This can take a few minutes.",
      };
    case "error":
      return {
        tone: "attention",
        label: "Sync is retrying",
        explanation: account.syncDetail ?? "The last sync didn't finish. MeeMail will try again.",
      };
    case "paused":
      return { tone: "paused", label: "Sync paused", explanation: account.syncDetail };
    case "gap":
    case "ok":
      return { tone: "ok", label: "Connected", explanation: null };
  }
}

/** Accounts that deserve a soft banner above the inbox. */
export function accountsNeedingAttention(accounts: readonly AccountSummary[]): AccountSummary[] {
  return accounts.filter(
    (a) => a.status === "needs_reauth" || a.status === "scope_missing" || a.openGaps.length > 0,
  );
}

const GAP_REASONS: Record<string, string> = {
  history_expired:
    "Gmail's change history had expired, so MeeMail re-checked that window directly.",
  offline: "It re-checked that window when it came back online.",
  resync: "MeeMail re-checked that window from scratch.",
};

export function gapExplanation(
  gap: AccountSummary["openGaps"][number],
  timeOptions: { locale?: string; timeZone?: string } = {},
): string {
  const from = formatShortDateTime(gap.fromAt, timeOptions);
  const to = formatShortDateTime(gap.toAt, timeOptions);
  const reason = GAP_REASONS[gap.reason] ?? (gap.reason ? `Reason: ${gap.reason}.` : "");
  return `Between ${from} and ${to}, mail may have been missed while MeeMail was offline. ${reason}`.trim();
}

export interface OAuthMessage {
  title: string;
  body: string;
}

export function oauthErrorMessage(code: string): OAuthMessage {
  switch (code) {
    case "no_refresh_token":
      return {
        title: "Google didn't hand over a lasting key",
        body: "This usually means MeeMail was connected to this Google account before. Open your Google Account → Security → Third-party access (“Your connections to third-party apps & services”), remove MeeMail, then connect again here.",
      };
    case "account_mismatch":
      return {
        title: "A different Google account signed in",
        body: "You were reconnecting one account but chose another at Google. Try again and pick the same address.",
      };
    case "invalid_state":
      return {
        title: "That sign-in link expired",
        body: "The Google sign-in took too long or was opened in another browser. Start again from this page.",
      };
    case "scope_missing":
      return {
        title: "A permission was left unticked",
        body: "On Google's consent screen, tick every checkbox (including reading and organising Gmail), then continue.",
      };
    case "exchange_failed":
      return {
        title: "Google's answer couldn't be completed",
        body: "Google finished, but MeeMail couldn't exchange the code. Check the server's Google client ID, secret and redirect URL, then try again.",
      };
    case "access_denied":
      return {
        title: "Connection cancelled",
        body: "You stopped at Google's screen. Nothing changed.",
      };
    default:
      return {
        title: "Connecting didn't finish",
        body: `Google or MeeMail reported "${code}". Try again; if it keeps happening, check the server logs.`,
      };
  }
}

/** Accounts plus a stable colour index per account id (by position). */
export function useAccountLookup() {
  const query = useAccounts();
  const data = query.data;
  const lookup = useMemo(() => {
    const list = [...(data ?? [])].sort((a, b) => a.position - b.position);
    const index = new Map(list.map((a, i) => [a.id, i]));
    const labels = accountLabels(list.map((a) => a.email));
    return {
      list,
      colorIndex: (id: string) => index.get(id) ?? 0,
      /** Short, unambiguous label for an account (by email). */
      label: (email: string) => labels.get(email),
      byId: (id: string) => list.find((a) => a.id === id),
    };
  }, [data]);
  return { query, ...lookup };
}
